/** Keep public campaign attribution while removing credentials and shipment queries. */
export function telemetryUrl(value: string): string {
  try {
    const url = new URL(value, 'https://shipzy.ai');
    url.username = ''; url.password = ''; url.hash = '';
    url.pathname = url.pathname.replace(/(\/(?:api\/)?(?:verify-claim|complete-profile|shared-tracking)\/)[^/]+/gi, '$1redacted')
      .replace(/(\/track\/shared\/)[^/]+/gi, '$1redacted');
    const campaign = new URLSearchParams();
    for (const [key, entry] of url.searchParams) {
      if (/^utm_(source|medium|campaign|content|term|id)$/.test(key) && /^[a-z0-9_ .-]{1,100}$/i.test(entry)) campaign.set(key, entry);
    }
    url.search = campaign.toString();
    return /^https?:\/\//i.test(value) ? url.toString() : url.pathname + url.search;
  } catch { return '[redacted-url]'; }
}

/** Telemetry payloads are JSON records. Retain diagnostics, redact sensitive values. */
export function privateTelemetry<T>(value: T, onTruncated?: () => void): T {
  const seen = new WeakSet<object>();
  let visited = 0;
  function clean(item: unknown, depth = 0): unknown {
    // SDK breadcrumbs can contain cyclic objects and shared subtrees. Bound the
    // walk so sanitizing a diagnostic cannot exhaust the application's memory.
    if (++visited > 10_000 || depth > 15) { onTruncated?.(); return '[redacted]'; }
    if (typeof item === 'string') {
      if (/^(https?:\/\/|\/)/i.test(item)) return telemetryUrl(item);
      return item.replace(/https?:\/\/[^\s"'<>]+/gi, telemetryUrl);
    }
    if (item && typeof item === 'object') {
      if (seen.has(item)) { onTruncated?.(); return '[repeated]'; }
      seen.add(item);
      if (item instanceof Date) return item.toISOString();
    }
    if (Array.isArray(item)) {
      if (item.length > 10_000) onTruncated?.();
      return item.slice(0, 10_000).map(entry => clean(entry, depth + 1));
    }
    if (item && typeof item === 'object') {
      const entries = Object.entries(item);
      if (entries.length > 10_000) onTruncated?.();
      return Object.fromEntries(entries.slice(0, 10_000).map(([key, entry]) => [key,
      /^(password|token|token_hash|access_token|refresh_token|secret|authorization|cookie|api_key|stripe-signature|postmark-token)$/i.test(key)
        ? '[redacted]' : clean(entry, depth + 1)]));
    }
    return item;
  }
  return clean(value) as T;
}
